import React, { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useNotification } from '@verzekeringapp-mono/shared/src/hooks/use_notification'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import {
  getNotificationsIsLoading,
  getNotificationsLastFetchedAt
} from '@verzekeringapp-mono/shared/src/state/selectors/notifications'
import { shouldRefetchNotifications } from '@verzekeringapp-mono/shared/src/utils/notifications'

type Props = {
  children: React.ReactNode
}

export default function NotificationsRefetchProvider ({ children }: Props) {
  const location = useLocation()
  const { getNotifications } = useNotification()
  const lastFetchedAt = useAppSelector(getNotificationsLastFetchedAt)
  const isLoading = useAppSelector(getNotificationsIsLoading)
  const isFetchingRef = useRef(false)

  const tryRefetchNotifications = useCallback(async () => {
    if (isLoading || isFetchingRef.current || !shouldRefetchNotifications(lastFetchedAt)) {
      return
    }

    isFetchingRef.current = true

    try {
      await getNotifications()
    } finally {
      isFetchingRef.current = false
    }
  }, [getNotifications, isLoading, lastFetchedAt])

  useEffect(() => {
    tryRefetchNotifications()
  }, [location.key, tryRefetchNotifications])

  useEffect(() => {
    const onFocus = () => {
      tryRefetchNotifications()
    }

    window.addEventListener('focus', onFocus)

    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [tryRefetchNotifications])

  return (
    <>
      {children}
    </>
  )
}
